import { ConciergePermissionsReporter } from '@/components/concierge-permissions-reporter';
import RouteChangeRenderTracking from '@/components/route-change-render-tracking';
import { useNavigationAudioSync } from '@/hooks/use-navigation-audio-sync';
import { initDatadogRum } from '@/libs/datadog/rum';
import { ConciergeProvider } from '@/providers/concierge';
import { DwAmplitudeProvider } from '@/providers/dw-amplitude';
import {
    ExperimentsProvider,
    ExperimentUserSyncProvider,
} from '@/providers/experiment-client';
import { FtueWorkflowProvider } from '@/providers/ftue-workflow';
import { JoinDialogProvider } from '@/providers/join-dialog';
import { LoadingDialogProvider } from '@/providers/loading-dialogs';
import { extractAppConfig } from '@/utils/app-config';
import { isChromiumBased } from '@/utils/browser';
import { CookieResponseDialog } from '@rhg/components/cookie-dialog';
import ErrorBoundary from '@rhg/components/error-boundary';
import HeartBeat from '@rhg/components/heartbeat';
import Page from '@rhg/components/page';
import { RegistrationDialogManager } from '@rhg/components/registration/dialog/manager';
import { TransitionDialogManager } from '@rhg/components/transition-partner/dialog-manager';
import { UserBlockerGate } from '@rhg/components/user-blocker-gate';
import { Environment } from '@rhg/constants';
import { AppConfigProvider } from '@rhg/hooks/use-app-config';
import { AnalyticsProvider, TrackDeviceType } from '@rhg/libs/analytics';
import { AnalyticsBaseContextProvider } from '@rhg/libs/analytics/base-context-provider';
import { createDestinations } from '@rhg/libs/analytics/destination';
import trackWebVitals from '@rhg/libs/analytics/track-web-vitals';
import Auth0Provider from '@rhg/libs/auth0/auth0-provider';
import { E2eAuthProvider } from '@rhg/libs/auth0/e2e-auth-provider';
import { queryClient, QueryClientProvider } from '@rhg/libs/react-query';
import { ReactQueryDevtools } from '@rhg/libs/react-query/devtools';
import { ActiveCircleIdProvider } from '@rhg/providers/active-circle-id';
import { AgeGenderDialogProvider } from '@rhg/providers/age-gender-dialog';
import { AudioProvider } from '@rhg/providers/audio';
import { AudioPlayerProvider } from '@rhg/providers/audio-player';
import { AutoSelectCircleProvider } from '@rhg/providers/auto-select-circle';
import { ChoiceDialogProvider } from '@rhg/providers/choice-dialog';
import { CircleAuthProvider } from '@rhg/providers/circle-auth';
import { CookieResponseProvider } from '@rhg/providers/cookie-response';
import { EnforcedThemeProvider } from '@rhg/providers/enforced-theme-provider';
import { GuidePlayerProvider } from '@rhg/providers/guide-player';
import { GuideQueueProvider } from '@rhg/providers/guide-queue';
import { HistoryProvider } from '@rhg/providers/history';
import { IntlWrapper } from '@rhg/providers/intl-wrapper';
import { InviteCodeProvider } from '@rhg/providers/invite-code';
import { MagicLinkProvider } from '@rhg/providers/magic-link';
import MediaPreferencesProvider from '@rhg/providers/media-preferences';
import { EmailOptInConfirmationProvider } from '@rhg/providers/notifications/email-opt-in-confirmation';
import { SnackbarProvider } from '@rhg/providers/snackbar';
import { StandaloneResultsValuesProvider } from '@rhg/providers/standalone-results-values';
import { ThemeSwitchProvider } from '@rhg/providers/theme-switch';
import { ToastDialogProvider } from '@rhg/providers/toast-dialog';
import { UpgradeHandoffClaimProvider } from '@rhg/providers/upgrade-handoff-claim';
import { VideoPlayerProvider } from '@rhg/providers/video-player';
import { NextPage } from 'next';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { ReactElement, ReactNode, useMemo } from 'react';

initDatadogRum();

const {
    NEXT_PUBLIC_ENV: env,
    NEXT_PUBLIC_AMPLITUDE_BASEURL: amplitudeBaseUrl,
    NEXT_PUBLIC_AMPLITUDE_TOKEN: amplitudeToken,
    NEXT_PUBLIC_AMPLITUDE_CH_EVENTS_PROXY_URL: chEventsProxyUrl,
    NEXT_PUBLIC_FF_CH_EVENTS_PROXY_ENABLED: chEventsProxyEnabled,
    NEXT_PUBLIC_ANALYTICS_DEBUG: debug,
} = extractAppConfig();

const destinations = createDestinations({
    amplitudeToken,
    amplitudeBaseUrl,
    chEventsProxyEnabled,
    chEventsProxyUrl,
    env: env as Environment,
    debug,
});

function OuterProviders({ children }: { children: ReactNode }) {
    const appConfig = useMemo(extractAppConfig, []);
    return (
        <HistoryProvider>
            <AppConfigProvider value={appConfig}>
                <ExperimentsProvider>
                    <QueryClientProvider client={queryClient}>
                        <CookieResponseProvider>
                            <StandaloneResultsValuesProvider>
                                <AuthProviders>
                                    <ActiveCircleIdProvider>
                                        <CircleAuthProvider>
                                            <ExperimentUserSyncProvider>
                                                <AnalyticsProviders>
                                                    <IntlWrapper>
                                                        <ThemeProviders>
                                                            <ErrorBoundary
                                                                appConfig={
                                                                    appConfig
                                                                }
                                                            >
                                                                {children}
                                                                <HeartBeat />
                                                            </ErrorBoundary>
                                                            <ReactQueryDevtools
                                                                buttonPosition="bottom-left"
                                                                initialIsOpen={
                                                                    false
                                                                }
                                                            />
                                                        </ThemeProviders>
                                                    </IntlWrapper>
                                                </AnalyticsProviders>
                                            </ExperimentUserSyncProvider>
                                        </CircleAuthProvider>
                                    </ActiveCircleIdProvider>
                                </AuthProviders>
                            </StandaloneResultsValuesProvider>
                        </CookieResponseProvider>
                    </QueryClientProvider>
                </ExperimentsProvider>
            </AppConfigProvider>
        </HistoryProvider>
    );
}

function ThemeProviders({ children }: { children: ReactNode }) {
    return (
        <EnforcedThemeProvider>
            <ThemeSwitchProvider>{children}</ThemeSwitchProvider>
        </EnforcedThemeProvider>
    );
}

function AuthProviders({ children }: { children: ReactNode }) {
    return (
        <E2eAuthProvider>
            <Auth0Provider>{children}</Auth0Provider>
        </E2eAuthProvider>
    );
}

function AnalyticsProviders({ children }: { children: ReactNode }) {
    return (
        <AnalyticsBaseContextProvider>
            <AnalyticsProvider destinations={destinations}>
                <ConciergePermissionsReporter />
                <TrackDeviceType>
                    {/* Intentionally outside consent-gated AnalyticsProvider init:
                    DW-only forwarding is migration-critical event delivery. */}
                    <DwAmplitudeProvider>{children}</DwAmplitudeProvider>
                </TrackDeviceType>
            </AnalyticsProvider>
        </AnalyticsBaseContextProvider>
    );
}

function BaseDialogProviders({ children }: { children: ReactNode }) {
    return (
        <SnackbarProvider>
            <LoadingDialogProvider>
                <ToastDialogProvider>
                    <ChoiceDialogProvider>{children}</ChoiceDialogProvider>
                </ToastDialogProvider>
            </LoadingDialogProvider>
        </SnackbarProvider>
    );
}

// Rendered inside AudioProvider so useNavigationAudioSync can access audio context.
function NonChromiumNavigationAudioSync() {
    useNavigationAudioSync();
    return null;
}

function NavigationAudioSync() {
    if (isChromiumBased()) return null;
    return <NonChromiumNavigationAudioSync />;
}

function BaseMediaProviders({ children }: { children: ReactNode }) {
    return (
        <MediaPreferencesProvider>
            <AudioProvider>
                <NavigationAudioSync />
                <AudioPlayerProvider>
                    <VideoPlayerProvider>{children}</VideoPlayerProvider>
                </AudioPlayerProvider>
            </AudioProvider>
        </MediaPreferencesProvider>
    );
}

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
    const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

    return (
        <OuterProviders>
            <CookieResponseDialog />
            <RouteChangeRenderTracking />
            <AutoSelectCircleProvider>
                <MagicLinkProvider>
                    <BaseMediaProviders>
                        <InviteCodeProvider>
                            <BaseDialogProviders>
                                <JoinDialogProvider>
                                    <AgeGenderDialogProvider>
                                        <FtueWorkflowProvider>
                                            <ConciergeProvider>
                                                <EmailOptInConfirmationProvider>
                                                    <GuideQueueProvider>
                                                        <GuidePlayerProvider>
                                                            <UserBlockerGate>
                                                                <Page
                                                                    {...pageProps}
                                                                >
                                                                    {getLayout(
                                                                        <Component
                                                                            {...pageProps}
                                                                        />
                                                                    )}
                                                                </Page>
                                                                <UpgradeHandoffClaimProvider>
                                                                    <RegistrationDialogManager />
                                                                    <TransitionDialogManager />
                                                                </UpgradeHandoffClaimProvider>
                                                            </UserBlockerGate>
                                                        </GuidePlayerProvider>
                                                    </GuideQueueProvider>
                                                </EmailOptInConfirmationProvider>
                                            </ConciergeProvider>
                                        </FtueWorkflowProvider>
                                    </AgeGenderDialogProvider>
                                </JoinDialogProvider>
                            </BaseDialogProviders>
                        </InviteCodeProvider>
                    </BaseMediaProviders>
                </MagicLinkProvider>
            </AutoSelectCircleProvider>
        </OuterProviders>
    );
};

export const reportWebVitals = (metric: NextWebVitalsMetric) => {
    trackWebVitals(metric, destinations);
};

export default App;
